import { useCallback, useReducer } from 'react';
import AuthenticatedController from '@mm/state/controllers/AuthenticatedController';
import { Pagination } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';

export type Section = 'overview' | 'q_and_a';

export type Doc = {
  id: number;
  title: string;
  subTitle: string;
  description: string;
  url: string;
  attachmentUrl: string;
  assetId: number | null;
  section: Section;
  sortOrder: number;
  hidden: boolean;
};

type DocsResponse = {
  items: Doc[];
  pagination?: Pagination;
};

export class HelpController {
  static retrieve(
    params: {
      section: Section;
    } & Partial<Pick<Pagination, 'page' | 'perPage'>>,
  ): Promise<DocsResponse> {
    return AuthenticatedController.get(API_URL + 'help', params);
  }

  static post(params: {
    email: string;
    comment: string;
    name: string;
    assetId?: number;
  }): Promise<any> {
    return AuthenticatedController.post(API_URL + 'feedback', {
      ...params,
      platform: 'desktop',
    });
  }
}

type State = {
  busy: boolean;
  ready: boolean;
  menu: 'guides' | 'qa' | 'feedback';
  activeId: number | undefined;
  qa: DocsResponse;
  guides: DocsResponse;
  feedback: {
    busy?: boolean;
    success?: boolean;
    text?: string;
    email: {
      email: string;
      isValid: boolean;
    };
  };
};

const defaults: State = {
  ready: false,
  busy: false,
  menu: 'guides',
  activeId: undefined,
  qa: { items: [] },
  guides: { items: [] },
  feedback: {
    busy: false,
    success: false,
    text: '',
    email: {
      email: '',
      isValid: false,
    },
  },
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'navigate':
      return {
        ...state,
        menu: action.payload,
        activeId: undefined,
      };
    case 'open':
      return {
        ...state,
        activeId: action.payload,
      };

    case 'retrieved':
      return {
        ...state,
        [action.payload.menu]: action.payload.data,
      };

    case 'feedback':
      return {
        ...state,
        feedback: {
          ...state.feedback,
          text: action.payload,
        },
      };

    case 'email':
      return {
        ...state,
        feedback: {
          ...state.feedback,
          email: action.payload,
        },
      };

    case 'submit':
      return {
        ...state,
        feedback: {
          ...state.feedback,
          busy: true,
        },
      };

    case 'ready':
      return {
        ...state,
        ready: true,
      };

    case 'submitEnd':
      return {
        ...state,
        feedback: {
          ...state.feedback,
          busy: false,
          success: !action.payload.err,
        },
      };

    default:
      return state;
  }
};

const mapMenuToSection = (s: string): Section | undefined => {
  switch (s) {
    case 'guides':
      return 'overview';
    case 'qa':
      return 'q_and_a';
    case 'feedback':
    default:
      return undefined;
  }
};

export const useMyGoals = () => {
  const [state, dispatch] = useReducer(reducer, defaults);
  const session = useSelector(selectSessionSelector);

  // const section = useMemo(() => mapMenuToSection(state.menu), [state.menu]);

  const fetchSection = async (menu: string) => {
    const section = mapMenuToSection(menu);
    if (section) {
      try {
        const data = await HelpController.retrieve({ section });
        dispatch({ type: 'retrieved', payload: { data, menu } });
      } catch (err) {}
    }
  };

  const init = useCallback(() => {
    Promise.all([fetchSection('guides'), fetchSection('qa')])
      .then(() => dispatch({ type: 'ready' }))
      .catch(() => null);
  }, []);

  const submitFeedback = async () => {
    const {
      feedback: { text, email },
    } = state;
    if (text && email.isValid) {
      dispatch({ type: 'submit' });
      let err;
      try {
        await HelpController.post({
          name: session.firstName,
          comment: text,
          email: email.email,
        });
      } catch (e) {
        err = e;
      } finally {
        dispatch({ type: 'submitEnd', payload: { err } });
      }
    }
  };

  return [state, dispatch, { init, submitFeedback }];
};
